import { ActionCreatorWithPayload, createAction } from "@reduxjs/toolkit";
import { Action, Middleware, MiddlewareAPI, UnknownAction } from "redux";

import type {
    RealtimeSocketActionEnvelope,
    RealtimeSocketResponseEnvelope,
} from "../../data/models/schemas";
import type { ReduxActionMeta } from "../types/ActionMeta";
import { send, ws } from "../websocket";

export const SOCKET_EVENT_NAME = "redux_event";
export const SOCKET_EVENT_RESPONSE_NAME = "redux_response";

export type ISocketMiddlewareAction<P> = Action & {
    socket: {
        types: string[];
    };
    payload?: P;
    meta?: ReduxActionMeta;
};

export type ISocketActionArguments = {
    /** Server-side realtime action name. */
    type: string;
    /** Optional client-only prefix for REQUEST/UPDATE/SUCCESS/ERROR actions. */
    responseTypePrefix?: string;
    meta?: ReduxActionMeta;
};

export type ISocketActionCreator<P> = (
    payload?: P
) => ISocketMiddlewareAction<P>;

export type ICreateSocketAction<P, S, E> = {
    action: ISocketActionCreator<P>;

    request: ActionCreatorWithPayload<S, string>;
    update: ActionCreatorWithPayload<S, string>;
    success: ActionCreatorWithPayload<S, string>;
    error: ActionCreatorWithPayload<E, string>;

    type_request: string;
    type_update: string;
    type_success: string;
    type_error: string;
};

/**
 * Generate action creators for socket action with actions for handling
 * REQUEST/UPDATE,SUCCESS/ERROR subsequent actions.
 * @typeParam P - The type of the payload body
 * @typeParam S - The type of the payload the 'success' reducer accepts
 * @typeParam E - The type of the payload the 'error' reducer accepts
 * @param args - The action arguments
 * @returns Generated actions for the network call and for handling redux callbacks
 * @public
 */
export const createSocketAction = <P = void, S = void, E = void>(
    args: ISocketActionArguments
): ICreateSocketAction<P, S, E> => {
    const responseTypePrefix = args.responseTypePrefix ?? args.type;
    const type_request = `${responseTypePrefix}_REQUEST`;
    const type_update = `${responseTypePrefix}_UPDATE`;
    const type_success = `${responseTypePrefix}_SUCCESS`;
    const type_error = `${responseTypePrefix}_ERROR`;

    return {
        action: (payload?: P): ISocketMiddlewareAction<P> => ({
            type: args.type,
            socket: {
                types: [type_request, type_update, type_success, type_error],
            },
            payload,
            ...(args.meta ? { meta: args.meta } : {}),
        }),

        request: createAction<S>(type_request) as ActionCreatorWithPayload<
            S,
            string
        >,
        update: createAction<S>(type_update) as ActionCreatorWithPayload<
            S,
            string
        >,
        success: createAction<S>(type_success) as ActionCreatorWithPayload<
            S,
            string
        >,
        error: createAction<E>(type_error) as ActionCreatorWithPayload<
            E,
            string
        >,

        type_request,
        type_update,
        type_success,
        type_error,
    };
};

export const websocketMiddleware: Middleware =
    (store) => (next) => (action: ISocketMiddlewareAction<unknown>) => {
        if (action && action.socket) {
            const state = store.getState() as {
                user?: { socket_token?: string };
            };

            const socketAction: RealtimeSocketActionEnvelope = {
                type: action.type,
                response_types: action.socket.types,
                token: state.user?.socket_token,
                payload: action.payload
                    ? JSON.stringify(action.payload)
                    : undefined,
            };

            send(JSON.stringify(socketAction));
        }

        return next(action);
    };

export const attachWebsocketListener = (store: MiddlewareAPI): void => {
    ws.onmessage = (message) => {
        // Deserialize JSON payload
        if (message.data) {
            const action = JSON.parse(
                message.data.toString()
            ) as RealtimeSocketResponseEnvelope;
            store.dispatch(action as UnknownAction);
        }
    };
};
